﻿import React, { useEffect, useState } from 'react';

import { GMetrixAccordion, GMetrixAccordionItem } from 'components/GMetrixAccordion/GMetrixAccordion';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { NewUser, OldPermission, Role, User, UsersApi } from 'apis/UsersApi';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { TileContainer } from 'components/TileContainer/TileContainer';
import { PartnerPermissions } from 'bundles/partnersDefault';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { type UserPermissions } from 'pages/users';
import { Col, Row } from 'react-bootstrap';

import 'components/SelectRoles/select-roles.scss';

type SelectRolesProps = {
    externalSave?: boolean;
    handlerType: 'Create' | 'Update';
    handleUserUpdate?: (userDetails: User) => void;
    newPermissionList: string[];
    parentAvailableRoles?: Role[];
    partnerId: number | undefined;
    partnerType?: number;
    permissions: UserPermissions | PartnerPermissions;
    setNewPermissionList: React.Dispatch<React.SetStateAction<string[]>>;
    setParentAvailableRoles?: React.Dispatch<React.SetStateAction<Role[]>>;
    user: User | NewUser;
};

export const SelectRoles = ({
    externalSave,
    handlerType,
    handleUserUpdate,
    newPermissionList,
    parentAvailableRoles,
    partnerId,
    partnerType,
    permissions,
    setNewPermissionList,
    setParentAvailableRoles,
    user
}: SelectRolesProps) => {

    const { formatMessage } = useIntl();
    const { catchWithToast } = useNotification();
    const [availablePermissions, setAvailablePermissions] = useState<OldPermission[]>([]);
    const [loadingAvailableRoles, setLoadingAvailableRoles] = useState(true);
    const [usersCurrentPermissions, setUsersCurrentPermissions] = useState<string[]>([]);
    const [usersCurrentPermissionsOriginal, setUsersCurrentPermissionsOriginal] = useState<string[]>([]);

    useEffect(() => {

        setNewPermissionList(usersCurrentPermissions);

    }, [usersCurrentPermissions]);

    useEffect(() => {

        // Pass the partner type explicitly when the caller knows it (e.g. creating
        // a user under a specific partner). Otherwise send 0 so the server uses the
        // partner the user is currently managing (SelectedPartner) instead of the
        // old URL ?PType= param, which is absent on the current routes and used to
        // silently fall back to the SuperAdmin (role 1) tree.
        const requestedPartnerType = partnerType ?? 0;

        // TEMP DEBUG: verify which permission tree we ask for / receive.
        // requestedPartnerType 0 => server falls back to the managed partner (SelectedPartner).
        // eslint-disable-next-line no-console
        console.log('[SelectRoles] requesting permissions for partnerType:', requestedPartnerType, '(0 = server uses currently-managed partner)');

        UsersApi.GetAllPermissions(requestedPartnerType)
            .then(oldPermissions => {

                // eslint-disable-next-line no-console
                console.log('[SelectRoles] received permission sections:', oldPermissions.map(p => p.Name));
                setAvailablePermissions(setInitialCheckboxes(oldPermissions, handlerType === 'Create' ? true : null));

            })
            .finally(() => setTimeout(() => {

                setLoadingAvailableRoles(false);

            }, 250)
            )
            .catch((error: unknown) => catchWithToast(error, formatMessage({ id: 'failed_to_load_roles.df2fb003', defaultMessage: 'Failed to load roles.' })));

    }, []);

    const editUserPermissionsOnClick = async () => {

        try {

            await UsersApi.Update(user, usersCurrentPermissions.toString(), partnerId ?? -1);
            setUsersCurrentPermissionsOriginal(usersCurrentPermissions);
            handleUserUpdate && handleUserUpdate(user);

        } catch (e) {

            catchWithToast(e, formatMessage({ id: 'failed_to_update_user_permissions.e98da741', defaultMessage: 'Failed to update user permissions.' }));

        }

    };

    const setInitialCheckboxes = (allPermissions: OldPermission[], selectAll: true | null = null) => {

        const currentPermissionMenuOptions: string[] = [];
        allPermissions = allPermissions.map(oldPermission => {

            let permissionGroup = true;

            oldPermission.References.map(ref => ref.actions.map(action => {

                if (selectAll === true) {

                    currentPermissionMenuOptions.push(action.MenuOptionID.toString());
                    action.checked = true;

                    return action;

                } else {

                    const targetedCheckbox = user.Permissions.find(userPerm => parseInt(userPerm.PermissionId) === action.MenuOptionID);

                    if (targetedCheckbox) {

                        currentPermissionMenuOptions.push(action.MenuOptionID.toString());
                        action.checked = true;

                    } else {

                        permissionGroup = false;
                        action.checked = false;

                    }
                    return action;

                }

            }));
            oldPermission.checked = permissionGroup;
            return oldPermission;

        });

        setUsersCurrentPermissions([...currentPermissionMenuOptions]);
        selectAll === null && setUsersCurrentPermissionsOriginal([...currentPermissionMenuOptions]);
        return allPermissions;

    };

    const checkPermissionBox = (e: any, name: string) => {

        let currentPermissions: string[] = usersCurrentPermissions;
        const newAvailablePermissions = availablePermissions.map(oldPermission => {

            if (oldPermission.Name === name) {

                const permissionGroup = e.target.checked;

                oldPermission.References.map(ref => ref.actions.map(action => {

                    if (e.target.checked) {

                        action.checked = true;
                        currentPermissions.push(action.MenuOptionID.toString());

                    } else {

                        currentPermissions = currentPermissions.filter(currPer => currPer !== action.MenuOptionID.toString());
                        action.checked = false;

                    }
                    return action;

                }));
                oldPermission.checked = permissionGroup;

            }
            return oldPermission;

        });
        setUsersCurrentPermissions([...new Set(currentPermissions)]);
        setAvailablePermissions(newAvailablePermissions);

    };

    const checkPermission = (e: any, menuOption: number) => {

        let currentPermissions: string[] = usersCurrentPermissions;
        const newAvailablePermissions = availablePermissions.map(oldPermission => {

            let permissionGroup = true;
            const value = e.target.checked;
            oldPermission.References.map(ref => ref.actions.map(action => {

                const targetedCheckbox = action.MenuOptionID === menuOption;
                if (targetedCheckbox) {

                    if (value) {

                        currentPermissions.push(action.MenuOptionID.toString());

                    } else {

                        currentPermissions = currentPermissions.filter(currPer => currPer !== action.MenuOptionID.toString());

                    }
                    action.checked = value;

                }

                if (!action.checked) {

                    permissionGroup = false;

                }
                return action;

            }));
            oldPermission.checked = permissionGroup;
            return oldPermission;

        });
        setUsersCurrentPermissions([...new Set(currentPermissions)]);
        setAvailablePermissions(newAvailablePermissions);

    };

    return (
        <TileContainer
            addButtonCustom={handlerType === 'Update' && !externalSave ? (
                <GMetrixButton
                    disabled={usersCurrentPermissions.length === usersCurrentPermissionsOriginal.length}
                    iconClass="fa-save fa-solid"
                    onClick={editUserPermissionsOnClick}

                    variant="primary"
                >
                    <FormattedMessage defaultMessage="Save" id="save.d44082ae" />
                </GMetrixButton>
            ) : undefined}
            addButton={handlerType === 'Update' && !externalSave}
            showHr={false}
            title={formatMessage({ id: 'permissions.076a2862', defaultMessage: 'Permissions' })}
        >
            <GMetrixAccordion alwaysOpen={false} className="select-roles-permissions" defaultActiveKey="0">
                {availablePermissions.map((permissionBox, index) => (
                    <GMetrixAccordionItem
                        bordered
                        title={(
                            <div className="d-flex align-items-center w-100">
                                <div className="d-flex align-items-center">
                                    {(() => {

                                        const total = permissionBox.References.reduce((acc, ref) => acc + ref.actions.length, 0);
                                        const checked = permissionBox.References.reduce(
                                            (acc, ref) => acc + ref.actions.filter(a => a.checked).length,
                                            0
                                        );
                                        return (
                                            <>
                                                <GMetrixCheckInput
                                                    checked={permissionBox.checked}
                                                    disabled={!permissions.Edit || !permissions.UserEdit}
                                                    label={permissionBox.Name}
                                                    onChange={e => checkPermissionBox(e, permissionBox.Name)}
                                                    type="checkbox"
                                                />
                                                <span className="ms-2 mb-1 small text-muted">
                                                    (
                                                    {checked}
                                                    {' '}
                                                    /
                                                    {' '}
                                                    {total}
                                                    )
                                                </span>
                                            </>
                                        );

                                    })()}
                                </div>
                            </div>
                        )}
                        eventKey={index.toString()}
                        key={permissionBox.Name}
                    >
                        {permissionBox.References.map(ref => ref.actions.map(action => (
                            <GMetrixCheckInput
                                checked={action.checked}
                                disabled={!permissions.Edit || !permissions.UserEdit}
                                key={action.MenuOptionID}
                                label={action.action}
                                onChange={e => checkPermission(e, action.MenuOptionID)}
                                type="checkbox"
                            />
                        ))
                        )}
                    </GMetrixAccordionItem>
                ))}
            </GMetrixAccordion>

        </TileContainer>
    );

};
